import { useParams } from "react-router-dom";
import { useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useProductContext } from "src/pages/Product/ProductDetails/ProductContext.tsx";
import { useGetOneProduct } from "src/api/client-api/products/useGetOneProduct.ts";
import { useGetOneScore } from "src/api/sure-api/score/useGetOneScore.ts";
import { Loading } from "src/components/utils/Loading.tsx";
import { ScoreDetailContextProvider } from "src/context/ScoreDetail/provider.tsx";
import { URLHandler } from "src/services/URLHandler";
import { useEntityBasedScoreParams } from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { ScoreDetailBuilder } from "src/context/ScoreDetail/ScoreDetailBuilder";
import { MissingQuestionnaire } from "src/components/SSQ/MissingQuestionnaire";
import { useTranslation } from "react-i18next";

type ProductScoreContextProviderProps = {
    children: JSX.Element | JSX.Element[];
};
const ProductScoreContextProvider = (props: ProductScoreContextProviderProps) => {
    const { children } = props;
    const { t } = useTranslation();

    const { selectedCompany, currentCampaign, getScoresByType } = useFoodpilotContext();

    const { product, ssq, post } = useProductContext();
    const { scoreId: scoreIdString } = useParams<{ scoreId: string }>();
    const scoreId = Number(scoreIdString);
    const { score: detailedScore } = useGetOneScore(scoreId);

    const { query, state } = useEntityBasedScoreParams({
        campaign: currentCampaign?.id,
        initDimensions: {
            dimensions: [["headings", "subheadings", "properties", "subproperties", "multivalues"], ["lca"]],
        },
        // initVariations: selectedCompanyVariations,
    });

    const { product: detailedProduct } = useGetOneProduct(product?.id, {
        company_id: selectedCompany?.id,
        scoreQuery: query,
        enabled: detailedScore !== undefined,
    });

    const { scores, prevScores } = detailedProduct || {};

    if (!detailedProduct || !detailedScore || !scores) {
        return <Loading />;
    }

    if (product === undefined) {
        return <Loading />;
    }
    if (ssq === undefined || post === undefined) {
        return <MissingQuestionnaire label={t("product.no_questionnaire_info")} />;
    }

    const allScores = getScoresByType("product");

    const scoreDetailBuilder = new ScoreDetailBuilder({
        post: post,
        scoreId: detailedScore.id,
        scores: { scores, prevScores },
        ssq: ssq,
        scorePrecision: detailedScore.precision,
    });

    if (selectedCompany === null) return;
    return (
        <ScoreDetailContextProvider
            scores={scores}
            prevCampaign={detailedProduct.prevCampaignName}
            scoreDetail={detailedScore}
            goBackLink={URLHandler.product.scores(selectedCompany?.id, product.id)}
            scoreDetailBuilder={scoreDetailBuilder}
            allScores={allScores}
            scoreState={state}
            tabConfig={{
                distribution: {
                    link: (scoreId) =>
                        URLHandler.product.scoreDistribution(selectedCompany?.id, product.id, scoreId.toString()),
                },
            }}
        >
            {children}
        </ScoreDetailContextProvider>
    );
};

export default ProductScoreContextProvider;
